import { DOCUMENT, Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Tracks the virtual keyboard height on mobile by listening to the
 * VisualViewport API and writes it to the CSS variable `--keyboard-inset-bottom`.
 *
 * Also toggles a body-level `keyboard-open` class so global CSS can hide
 * elements (e.g. the fixed bottom navbar) while the keyboard is up. CSS-only
 * conditionals on var() values aren't possible, so a class flag is required.
 *
 * Usage in CSS:
 *   .my-modal { max-height: calc(100dvh - var(--keyboard-inset-bottom, 0px)); }
 *   .my-actions { position: sticky; bottom: var(--keyboard-inset-bottom, 0px); }
 *   body.keyboard-open .navbar { display: none; }
 *
 * Browsers without VisualViewport (mostly older Safari) get a no-op:
 * the var stays at 0, layout falls back to the static positioning.
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {
  private initialised = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone
  ) {}

  /** Idempotent — safe to call from app bootstrap. */
  init(): void {
    if (this.initialised || !isPlatformBrowser(this.platformId)) {
      return;
    }
    const win = this.document.defaultView;
    if (!win || !win.visualViewport) {
      return;
    }
    this.initialised = true;

    // Run outside Angular zone — these events fire continuously while the
    // keyboard animates; we only mutate CSS, no need for change detection.
    const vv = win.visualViewport;
    this.zone.runOutsideAngular(() => {
      // Threshold for considering the keyboard "open" via viewport math.
      // Below ~120px it's typically just a soft-keyboard sliver or address-bar
      // collapse, not a real keyboard — toggling the class on those events
      // would flicker the bottom nav unnecessarily.
      //
      // NOTE: viewport-shrink detection is unreliable on Chromium with
      // `interactive-widget=resizes-content` (set in index.html) because the
      // *layout* viewport itself shrinks, leaving --keyboard-inset-bottom at 0.
      // We therefore ALSO toggle keyboard-open on focusin/focusout of editable
      // elements as a deterministic primary signal. The viewport listener
      // remains as a backup (e.g. iOS Safari, hardware keyboards).
      const KEYBOARD_OPEN_THRESHOLD_PX = 120;
      const root = this.document.documentElement;
      const body = this.document.body;
      let focusedField: HTMLElement | null = null;
      let focusoutTimer: ReturnType<typeof setTimeout> | null = null;

      const setKeyboardOpen = (open: boolean): void => {
        const wasOpen = body.classList.contains('keyboard-open');
        body.classList.toggle('keyboard-open', open);
        // Scroll on the closed → open transition (only when we have a target).
        // Guard: only scroll on mobile (≤768px) — on desktop there is no virtual
        // keyboard, so the 300ms scrollIntoView would shift open overlays/dropdowns.
        // Delay past the virtual keyboard animation (~300ms on most mobile browsers)
        // so scrollIntoView uses the final reduced viewport, not the pre-keyboard height.
        if (!wasOpen && open && focusedField && win.innerWidth <= 768) {
          const target = focusedField;
          setTimeout(() => {
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 300);
        }
      };

      const update = (): void => {
        // Distance from the bottom of the layout viewport to the bottom of
        // the visual viewport. Positive when the keyboard is up.
        const inset = Math.max(0, win.innerHeight - vv.height - vv.offsetTop);
        root.style.setProperty('--keyboard-inset-bottom', `${inset}px`);
        // Only flip to open via viewport math; never flip closed here, because
        // on Chromium-resizes-content the inset stays 0 even with keyboard up.
        // focusout handler is responsible for closing.
        if (inset > KEYBOARD_OPEN_THRESHOLD_PX) {
          setKeyboardOpen(true);
        }
      };

      // Track focus on inputs / textareas / contenteditable so we know what
      // to scroll into view AND so we can deterministically toggle the
      // keyboard-open class regardless of viewport resize behaviour.
      const isEditable = (el: EventTarget | null): el is HTMLElement => {
        if (!(el instanceof HTMLElement)) {
          return false;
        }
        const tag = el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return true;
        }
        return el.isContentEditable;
      };
      this.document.addEventListener('focusin', (ev: FocusEvent) => {
        if (focusoutTimer !== null) {
          clearTimeout(focusoutTimer);
          focusoutTimer = null;
        }
        if (isEditable(ev.target)) {
          focusedField = ev.target;
          setKeyboardOpen(true);
        }
      });
      this.document.addEventListener('focusout', () => {
        // Delay close so focus jumps between inputs (or to a Material overlay)
        // don't flicker the nav back on. 200ms is enough to bridge focus
        // changes but invisible to the user.
        if (focusoutTimer !== null) {
          clearTimeout(focusoutTimer);
        }
        focusoutTimer = setTimeout(() => {
          focusedField = null;
          setKeyboardOpen(false);
          focusoutTimer = null;
        }, 200);
      });

      update();
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    });
  }
}
